import React from 'react';
import { RegisterOptions, useForm } from 'react-hook-form';
import ExclamationMark from '../../../visuals/Icons/Old/ExclamationMark';
import Button, { IButton } from '../../Button';
import Input, { IInput } from '../Input';

interface IInputWithName extends IInput {
  name: string;
}

interface ButtonProps extends Omit<IButton, 'children'> {
  canBeDisabled?: boolean;
}

interface IInputWithButtonProps {
  inputProps: IInputWithName;
  buttonProps: ButtonProps;
  children: React.ReactNode;
  handleSubmit: (formData: { [key: string]: string }) => void;
  errorMessage?: string;
  registerOptions?: RegisterOptions;
}

const InputWithButton = ({
  inputProps,
  buttonProps: { canBeDisabled = true, ...buttonProps },
  children,
  handleSubmit,
  errorMessage,
  registerOptions,
}: IInputWithButtonProps) => {
  const {
    register,
    handleSubmit: handleSubmitForm,
    watch,
    formState: { isSubmitted, isValid, errors },
  } = useForm();
  const hasError = Boolean(errorMessage || errors[inputProps.name]);

  return (
    <>
      <form onSubmit={handleSubmitForm(handleSubmit)}>
        <Input
          {...inputProps}
          dataTestId={`InputWithButton-Input-${inputProps.name}`}
          hasError={hasError}
          withColoredBorder
          {...register(inputProps.name, registerOptions || { required: true })}
        />
        <Button
          onClick={buttonProps.onClick}
          size={buttonProps.size}
          disabled={
            (buttonProps.isLoading ||
              buttonProps.disabled ||
              !(inputProps.name && watch(inputProps.name))) &&
            canBeDisabled
          }
          block={false}
          hasMinWidth={false}
          variant={buttonProps.variant}
          ariaLabel={buttonProps.ariaLabel}
          isLoading={buttonProps.isLoading}
          type="submit"
          dataTestId={`InputWithButton-Button-${inputProps.name}`}
        >
          {children}
        </Button>
        {hasError && isSubmitted && !isValid && (
          <p className="error-message hzr">
            <ExclamationMark aria-hidden="true" />
            {errorMessage || (errors[inputProps.name]!.message as string)}
          </p>
        )}
      </form>
      <style jsx>{`
        form {
          display: grid;
          grid-template-columns: 2fr 1fr;
        }

        form > :global(input) {
          border-radius: var(--rd-5) 0 0 var(--rd-5);
          height: 42px;
        }

        form > :global(button) {
          border-radius: 0 var(--rd-5) var(--rd-5) 0;
          height: 42px;
        }

        .error-message {
          color: var(--clr-danger);
          margin: var(--space-1) var(--space-2);
          display: flex;
          align-items: center;
        }

        .error-message :global(svg) {
          margin-right: var(--space-1);
        }
      `}</style>
    </>
  );
};

export default InputWithButton;
